import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './api.js';
import type { ClienteHttp } from './api.js';

/**
 * Envio do certificado digital A1.
 *
 * Sem certificado não existe emissão: é ele que assina o XML e autentica a
 * conexão com a SEFAZ. Era o último passo do onboarding que só existia por
 * API, e o que mais trava cliente novo.
 *
 * O arquivo vai direto para o cofre cifrado — não passa por disco em claro, e
 * a senha não é guardada em lugar nenhum além do envelope do próprio cofre.
 * A validação (senha certa, CNPJ do certificado igual ao do emitente,
 * validade) acontece na API, que devolve o motivo em português.
 */
export function CertificateUpload({
  http,
  emitterId,
  nome,
  onFechar,
}: {
  http: ClienteHttp;
  emitterId: string;
  nome: string;
  onFechar: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const enviar2 = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('certificate', arquivo as File);
      form.append('password', senha);
      return http.apiForm<{ not_after: string }>(`/v1/emitters/${emitterId}/certificate`, form);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['emitters'] });
      onFechar();
    },
    onError: (err) =>
      setErro(err instanceof ApiError ? err.message : 'Não foi possível enviar o certificado.'),
  });

  function enviar(e: FormEvent): void {
    e.preventDefault();
    setErro(null);
    if (!arquivo) {
      setErro('Escolha o arquivo .pfx ou .p12 do certificado.');
      return;
    }
    enviar2.mutate();
  }

  return (
    <form className="card form-card" onSubmit={enviar}>
      <h3 style={{ margin: '0 0 4px' }}>Certificado digital de {nome}</h3>
      <p className="sub" style={{ margin: '0 0 16px' }}>
        Arquivo A1 (.pfx ou .p12) emitido para o mesmo CNPJ da empresa. O certificado em cartão ou token (A3)
        não serve: a assinatura acontece no servidor, sem o hardware por perto.
      </p>

      {erro && (
        <div className="alert bad" style={{ marginBottom: 14 }}>
          <b>Não deu para enviar</b>
          {erro}
        </div>
      )}

      <div className="grid-campos">
        <label className="campo">
          Arquivo do certificado
          <input
            className="input"
            type="file"
            accept=".pfx,.p12,application/x-pkcs12"
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
          />
        </label>

        <label className="campo">
          Senha do certificado
          <input
            className="input"
            type="password"
            autoComplete="off"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
          />
          <small className="campo-dica">Usada só para abrir o arquivo; não fica guardada em claro.</small>
        </label>
      </div>

      <div className="fix" style={{ marginTop: 16 }}>
        <button className="btn btn-primary" type="submit" disabled={enviar2.isPending}>
          {enviar2.isPending ? 'Enviando...' : 'Enviar certificado'}
        </button>{' '}
        <button className="btn btn-ghost" type="button" onClick={onFechar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
